import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { ForgeDrawer } from '@/components/forge/ForgeDrawer'
import { Icon } from '@/components/forge/Icon'
import { ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Mono, Num } from '@/components/forge/primitives'
import { useDeleteDocument, useDocument } from '@/features/documents/queries'
import { usePlaceNodes } from '@/features/hierarchy/queries'
import { useToast } from '@/providers/ToastProvider'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/cn'
import { KIND_ICON } from './DocumentAttachmentPicker'
import { DocThumb, RevisionStatusPill } from './DocumentsPage'

/**
 * One document, in three tabs.
 *
 * LIVE. Metadata, what it is attached to, and its revision history.
 *
 * ── Metadata is read here and edited in the form ─────────────────────────
 * The demo drawer's position was that metadata is read-only because it comes
 * from the system that filed the document — SharePoint, a vendor portal, a CAD
 * vault. That was true of the demo's fields: an owner, a language, a source, a
 * free-text revision label. None of them is a column, and none is here.
 *
 * What IS a column is the name, the type and the two dates, and those are this
 * app's to own — the API serves a `PUT` for exactly them. So Edit opens the
 * form dialog rather than putting nine inputs in a drawer, which keeps one
 * write path: `PUT` is a full replace of the attachments and the file id too,
 * and a drawer that edited a name in isolation would have to send both back
 * anyway.
 *
 * ── The Verified pill is gone; a revision status took its place ──────────
 * See `RevisionStatusPill`. Verification had no field; ingestion status is real
 * and had no reader until now.
 *
 * ── "Reaches" and "Usage" are gone ───────────────────────────────────────
 * The demo's Reach tab ran the inheritance resolver backwards over the estate
 * — "Site" is a word until it is nine named machines. The API has no
 * inheritance: attachments are explicit, and what a document reaches IS its
 * attachment list. So the tab is the attachment list, honestly named.
 *
 * Usage counted the open alerts and work orders on everything a document
 * reached. Both were computed off fixture collections joined on a fixture
 * asset id; against the real API they would be a request per attached machine,
 * to answer a question nothing on this screen acts on. Recorded in
 * BACKEND-GAPS.md rather than approximated.
 */

const TABS = ['metadata', 'attachments', 'revisions']

export function DocumentDrawer({ id, access, onClose, onEdit }) {
  const { t } = useTranslation(['app', 'common'])
  const { toast } = useToast()
  const [tab, setTab] = useState(TABS[0])
  const [confirming, setConfirming] = useState(false)

  const doc = useDocument(id)
  const remove = useDeleteDocument()
  const record = doc.data

  /* The owner plant's name — the record carries only its id. */
  const owner = usePlaceNodes(
    record?.ownerPlantId ? [{ tier: 'site', id: record.ownerPlantId }] : [],
  )
  const ownerName = record?.ownerPlantId
    ? (owner.data.items.find((node) => node.id === record.ownerPlantId)?.name ??
      (owner.isPending ? t('common:loading') : record.ownerPlantId))
    : null

  const close = () => {
    onClose()
    setTab(TABS[0])
    setConfirming(false)
  }

  return (
    <ForgeDrawer
      open={Boolean(id)}
      onOpenChange={(next) => !next && close()}
      title={record?.name ?? t('dataFusion.docs.drawerTitle')}
      closeLabel={t('common:actions.close')}
      className="w-[min(34rem,100vw)]"
    >
      {doc.isPending ? (
        <SkeletonRows count={4} />
      ) : doc.isError ? (
        <ErrorState
          error={doc.error}
          title={t('dataFusion.docs.loadOneFailed')}
          onRetry={doc.refetch}
        />
      ) : (
        <>
          <div className="-mx-5 -mt-4 flex items-start gap-3 border-b border-hairline px-5 py-4">
            <DocThumb doc={record} large />
            <div className="min-w-0 flex-1">
              <p className="m-0 text-body-sm text-ink-dim">
                {t(`dataFusion.docs.type.${record.documentType}`)}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <RevisionStatusPill revision={record.latestRevision} />
                <Mono className="text-micro text-ink-dim">
                  <Num>
                    {t('dataFusion.docs.revisionCount', { count: record.revisions.length })}
                  </Num>
                </Mono>
              </div>
            </div>

            {/* The current revision, opened in a new tab. A presigned address
                with a short life — see `useDocument` on why this response is
                never cached. */}
            {record.latestRevision ? (
              <ForgeButton asChild size="sm">
                {/* `asChild` and not an `as` prop: the primitive composes
                    through Radix's Slot, so the anchor keeps its own semantics
                    and the button keeps its styling. `noreferrer` because the
                    URL is presigned and its query string is the credential. */}
                <a href={record.latestRevision.viewUrl} target="_blank" rel="noreferrer">
                  <Icon name="eye" size="xs" />
                  {t('dataFusion.docs.openFile')}
                </a>
              </ForgeButton>
            ) : null}
          </div>

          <div
            role="tablist"
            aria-label={t('dataFusion.docs.tabsLabel')}
            className="-mx-5 mb-4 flex gap-1 border-b border-hairline px-5"
          >
            {TABS.map((entry) => (
              <button
                key={entry}
                type="button"
                role="tab"
                aria-selected={tab === entry}
                onClick={() => setTab(entry)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2.5 text-body-sm transition-colors',
                  tab === entry
                    ? 'border-forge font-semibold text-ink'
                    : 'border-transparent text-ink-dim hover:text-ink',
                )}
              >
                {t(`dataFusion.docs.tabs.${entry}`)}
              </button>
            ))}
          </div>

          {tab === 'metadata' ? (
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2.5">
              <Row label={t('dataFusion.docs.fields.name')}>{record.name}</Row>
              <Row label={t('dataFusion.docs.fields.type')}>
                {t(`dataFusion.docs.type.${record.documentType}`)}
              </Row>
              {/* Who it belongs to and how deep it reaches — both fixed when it
                  was filed. */}
              <Row label={t('dataFusion.docs.fields.owner')}>
                {record.ownerPlantId ? ownerName : t('dataFusion.docs.owner.account')}
              </Row>
              <Row label={t('dataFusion.docs.fields.level')}>
                {t(`dataFusion.docs.level.${record.level}`)}
              </Row>
              <Row label={t('dataFusion.docs.fields.effective')}>
                <Num>{formatDateTime(record.effectiveDate, { dateStyle: 'medium' })}</Num>
              </Row>
              <Row label={t('dataFusion.docs.fields.expiry')}>
                {record.expiryDate ? (
                  <Num>{formatDateTime(record.expiryDate, { dateStyle: 'medium' })}</Num>
                ) : (
                  <span className="text-ink-mute italic">{t('dataFusion.docs.noExpiry')}</span>
                )}
              </Row>
              <Row label={t('dataFusion.docs.fields.revision')}>
                <Mono>
                  <Num>{record.currentRevision}</Num>
                </Mono>
              </Row>
              <Row label={t('dataFusion.docs.fields.filed')}>
                <Num>{formatDateTime(record.createdAt, { dateStyle: 'medium' })}</Num>
              </Row>

              {/*
                ── The demo's other seven fields ─────────────────────────
                Owner, revision label, language, source system, tag list, page
                count and file size. Every one was authored in the fixture and
                none is a column on `Document` or `DocumentRevision`. They are
                not shown as em dashes either — a blank row invites somebody to
                look for the control that fills it. Listed in BACKEND-GAPS.md.

                Restoring one means a `Row` per field, plus its key under
                `dataFusion.docs.fields`. The keys are deliberately NOT left in
                the locale file and the rows are not written out here even as a
                comment: `verify:i18n` scans for translation calls by regex and
                cannot tell a commented one from a live one, so a copied line
                would either fail the build or hold a key nothing uses.
              */}
            </dl>
          ) : null}

          {tab === 'attachments' ? (
            <>
              <p className="mt-0 mb-3 max-w-[60ch] text-body-sm text-ink-dim">
                {t('dataFusion.docs.attachmentsIntro')}
              </p>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {record.attachments.map((one) => (
                  <li
                    key={`${one.kind}-${one.id}`}
                    className="flex items-center gap-2.5 rounded-sm border border-hairline bg-card px-3 py-2"
                  >
                    <Icon name={KIND_ICON[one.kind]} size="sm" className="text-ink-mute" />
                    <span className="min-w-0 flex-1 truncate text-body-sm text-ink">
                      {one.name}
                    </span>
                    <span className="text-micro font-normal text-ink-dim">
                      {t(`dataFusion.docs.attachKind.${one.kind}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {tab === 'revisions' ? (
            <>
              <p className="mt-0 mb-3 max-w-[60ch] text-body-sm text-ink-dim">
                {t('dataFusion.docs.revisionsIntro')}
              </p>
              {/* Newest first, where the API sends them ascending. A history is
                  read from the current version backwards. */}
              <ol className="m-0 flex list-none flex-col gap-2 p-0">
                {[...record.revisions].reverse().map((revision) => (
                  <li
                    key={revision.id}
                    className="flex flex-wrap items-center gap-2.5 rounded-sm border border-hairline bg-card px-3 py-2.5"
                  >
                    <Mono className="text-body-sm font-semibold text-ink">
                      <Num>r{revision.revision}</Num>
                    </Mono>
                    <span className="text-micro font-normal text-ink-dim">
                      <Num>{formatDateTime(revision.createdAt, { dateStyle: 'medium' })}</Num>
                    </span>
                    <RevisionStatusPill revision={revision} />

                    <ForgeButton asChild size="sm" className="ms-auto">
                      <a href={revision.viewUrl} target="_blank" rel="noreferrer">
                        {t('dataFusion.docs.openFile')}
                      </a>
                    </ForgeButton>

                    {/* The engine's own sentence, shown because it is written
                        for a person — `error` is capped at 500 characters and
                        the model's comment says it is not a stack trace. */}
                    {revision.status === 'failed' && revision.error ? (
                      <p className="m-0 w-full text-body-sm text-on-red">{revision.error}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </>
          ) : null}

          {/* Edit opens for a manager of the owner plant, or — for the
              attachments alone — for anyone holding an attach key. Delete is the
              manager's only. */}
          {access.canEditAttachments(record) ? (
            <div className="mt-5 flex flex-wrap gap-2 border-t border-hairline pt-4">
              <ForgeButton onClick={() => onEdit(record)}>
                <Icon name="pencil" size="xs" />
                {access.canEditDocument(record)
                  ? t('common:actions.edit')
                  : t('dataFusion.docs.editAttachments')}
              </ForgeButton>
              {access.canDelete(record) ? (
                <ForgeButton variant="danger" onClick={() => setConfirming(true)}>
                  <Icon name="trash" size="xs" />
                  {t('common:actions.delete')}
                </ForgeButton>
              ) : null}
            </div>
          ) : null}

          {/* Deleting soft-deletes the document AND detaches every revision's
              file, so the whole history goes with it. Confirmed, because
              nothing on this screen restores one. */}
          <ForgeDialog
            open={confirming}
            onOpenChange={(next) => !next && setConfirming(false)}
            size="sm"
            title={t('dataFusion.docs.deleteTitle')}
            description={t('dataFusion.docs.deleteBody', {
              name: record.name,
              count: record.revisions.length,
            })}
            footer={
              <div className="flex flex-1 flex-wrap justify-end gap-2">
                <ForgeButton onClick={() => setConfirming(false)}>
                  {t('common:actions.cancel')}
                </ForgeButton>
                <ForgeButton
                  variant="danger"
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(record.id, {
                      onSuccess: () => {
                        setConfirming(false)
                        close()
                        toast(t('dataFusion.docs.toast.deleted', { name: record.name }))
                      },
                      onError: () => toast(t('dataFusion.docs.toast.deleteFailed'), 'error'),
                    })
                  }
                >
                  {t('common:actions.delete')}
                </ForgeButton>
              </div>
            }
          >
            <p className="m-0 text-body-sm text-ink-dim">
              {t('dataFusion.docs.deleteWarning')}
            </p>
          </ForgeDialog>
        </>
      )}
    </ForgeDrawer>
  )
}

/** One `dt`/`dd` pair. A definition list, because that is what this is. */
function Row({ label, children }) {
  return (
    <>
      <Eyebrow as="dt" className="pt-0.5">
        {label}
      </Eyebrow>
      <dd className="m-0 min-w-0 text-body-sm text-ink">{children}</dd>
    </>
  )
}
